import { HttpErrorResponse } from '@angular/common/http';

/**
 * Shared HTTP error response formatter.
 * Extracts actionable error messages from various response formats,
 * distinguishing network failures, authorization, throttling, and server errors.
 */
export class HttpErrorFormatter {
  /**
   * Extract a user-friendly error message from an HttpErrorResponse.
   * Prefers API-supplied messages in this order:
   * 1. RFC 7807 'message' field (custom)
   * 2. RFC 7807 'detail' field
   * 3. RFC 7807 'title' field
   * 4. Plain text response body
   * 5. Angular's err.message
   * 6. HTTP status text with fallback
   *
   * Returns object with message, status, and isRetryable flag.
   */
  static formatError(err: any): HttpErrorFormattedError {
    const status = err?.status ?? 0;

    // Network failure: status 0
    if (status === 0) {
      return {
        message: 'Unable to connect to the server. Please check your network connection and try again.',
        status: 0,
        isRetryable: true,
        isNetworkFailure: true
      };
    }

    // Authorization failures: 401/403
    if (status === 401) {
      return {
        message: 'Your session has expired. Please sign in again.',
        status: 401,
        isRetryable: false,
        isAuthFailure: true
      };
    }

    if (status === 403) {
      return {
        message: 'You do not have permission to perform this action. Contact your administrator if you believe this is an error.',
        status: 403,
        isRetryable: false,
        isAuthFailure: true
      };
    }

    // Rate limiting: 429
    if (status === 429) {
      const retryAfter = err?.headers?.get?.('Retry-After');
      const retrySeconds = retryAfter ? parseInt(retryAfter, 10) : 60;
      return {
        message: `Too many requests. Please wait ${retrySeconds} seconds and try again.`,
        status: 429,
        isRetryable: true,
        isThrottled: true,
        retryAfterSeconds: retrySeconds
      };
    }

    // RFC 7807 ProblemDetails or custom error response
    if (err?.error && typeof err.error === 'object') {
      // Custom message field (app-specific)
      if (err.error.message && typeof err.error.message === 'string') {
        return {
          message: err.error.message,
          status,
          isRetryable: status >= 500,
          detail: err.error.detail,
          traceId: err.error.traceId
        };
      }

      // RFC 7807 detail field
      if (err.error.detail && typeof err.error.detail === 'string') {
        return {
          message: err.error.detail,
          status,
          isRetryable: status >= 500,
          title: err.error.title,
          traceId: err.error.traceId
        };
      }

      // RFC 7807 title field
      if (err.error.title && typeof err.error.title === 'string') {
        return {
          message: err.error.title,
          status,
          isRetryable: status >= 500,
          traceId: err.error.traceId
        };
      }
    }

    // Plain text response body
    if (err?.error && typeof err.error === 'string' && err.error.trim()) {
      return {
        message: err.error,
        status,
        isRetryable: status >= 500
      };
    }

    // Angular HttpErrorResponse message
    if (err?.message && typeof err.message === 'string') {
      return {
        message: err.message,
        status,
        isRetryable: status >= 500
      };
    }

    // Server error (5xx)
    if (status >= 500) {
      return {
        message: 'An unexpected server error occurred. Please try again later.',
        status,
        isRetryable: true,
        traceId: err?.error?.traceId
      };
    }

    // Client error (4xx)
    if (status >= 400) {
      return {
        message: `Request failed with status ${status}. Please check your input and try again.`,
        status,
        isRetryable: false
      };
    }

    // Generic fallback
    return {
      message: 'An unexpected error occurred. Please try again.',
      status,
      isRetryable: status === 0 || status >= 500
    };
  }
}

/**
 * Formatted error response with metadata.
 */
export interface HttpErrorFormattedError {
  message: string;
  status: number;
  isRetryable: boolean;
  isNetworkFailure?: boolean;
  isAuthFailure?: boolean;
  isThrottled?: boolean;
  retryAfterSeconds?: number;
  detail?: string;
  title?: string;
  traceId?: string;
}
